/**
 * Serviço para gerenciar usuários
 */

import { getToken } from "./tokenService";
import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface ClientInfo {
  id: string;
  name: string;
  company: string;
}

export interface WorkspaceInfo {
  id: string;
  name: string;
  description?: string;
  client_id: string;
}

export interface PermissionInfo {
  id: number;
  name: string;
  display_name: string;
  description?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  access_type_id: number;
  access_type_name: string;
  status_id: number;
  status_name: string;
  profile_id?: string;
  profile_name?: string;
  invite_sent?: boolean;
  email_verified?: boolean;
  email_verified_at?: string;
  failed_login_attempts?: number;
  locked_until?: string;
  clients: ClientInfo[];
  workspaces: WorkspaceInfo[];
  permissions?: PermissionInfo[];
  created_at: string;
  updated_at: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    pages: number;
  };
}

export interface CreateUserRequest {
  name: string;
  email: string;
  access_type_id: number;
  profile_id?: string;
  password?: string;
  client_ids?: string[];
  workspace_ids?: string[];
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  access_type_id?: number;
  profile_id?: string;
  password?: string;
  client_ids?: string[];
  workspace_ids?: string[];
}

export interface UsersStats {
  total: number;
  active: number;
  pending: number;
  by_access_type: Array<{
    access_type: string;
    count: number;
  }>;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
    console.log("[apiRequest] Body enviado:", {
      method,
      endpoint,
      body: config.body,
    });
  } else if (method !== "GET") {
    console.warn(
      "[apiRequest] Aviso: método",
      method,
      "sem body para",
      endpoint
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista usuários com filtros e paginação
 */
export async function listUsers(options?: {
  limit?: number;
  offset?: number;
  status_id?: number;
  access_type_id?: number;
  profile_id?: string;
  search?: string;
}): Promise<ApiResponse<UsersListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.status_id)
    params.append("status_id", options.status_id.toString());
  if (options?.access_type_id)
    params.append("access_type_id", options.access_type_id.toString());
  if (options?.profile_id) params.append("profile_id", options.profile_id);
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<UsersListResponse>(
    `/users${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Obtém um usuário por ID
 */
export async function getUserById(id: string): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}`);
}

/**
 * Cria um novo usuário
 */
export async function createUser(
  user: CreateUserRequest
): Promise<ApiResponse<User>> {
  return apiRequest<User>("/users", "POST", user);
}

/**
 * Atualiza um usuário
 */
export async function updateUser(
  id: string,
  user: UpdateUserRequest
): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}`, "PUT", user);
}

/**
 * Remove um usuário
 */
export async function deleteUser(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/users/${id}`, "DELETE");
}

/**
 * Altera o status de um usuário
 */
export async function toggleUserStatus(
  id: string,
  statusId: number
): Promise<ApiResponse<User>> {
  // Validação do statusId
  if (!statusId || isNaN(statusId)) {
    return {
      success: false,
      error: {
        code: "INVALID_STATUS_ID",
        message: "status_id inválido ou não fornecido",
      },
    };
  }

  console.log("[toggleUserStatus] Enviando:", {
    id,
    statusId,
    body: { status_id: statusId },
  });

  return apiRequest<User>(`/users/${id}/toggle-status`, "POST", {
    status_id: statusId,
  });
}

/**
 * Reenvia convite para usuário
 */
export async function resendInvite(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/users/${id}/resend-invite`, "POST");
}

/**
 * Obtém estatísticas dos usuários
 */
export async function getUsersStats(): Promise<ApiResponse<UsersStats>> {
  return apiRequest<UsersStats>("/users/stats");
}

/**
 * Aprova um usuário pendente
 */
export async function approveUser(
  id: string,
  data: { profile_id?: string; client_ids?: string[] }
): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}/approve`, "POST", data);
}

/**
 * Rejeita um usuário pendente
 */
export async function rejectUser(id: string): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}/reject`, "POST");
}

/**
 * Retorna um usuário rejeitado para pendente
 */
export async function returnToPending(id: string): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}/return-to-pending`, "POST");
}

/**
 * Desbloqueia um usuário bloqueado
 */
export async function unblockUser(id: string): Promise<ApiResponse<User>> {
  return apiRequest<User>(`/users/${id}/unblock`, "POST");
}

/**
 * Obtém histórico de auditoria de um usuário
 */
export interface AuditHistoryEntry {
  id: string;
  date: string;
  action: string;
  action_type: string;
  action_user: string;
  action_user_email?: string;
  details: any;
  ip_address?: string;
}

export async function getUserAuditHistory(
  id: string
): Promise<ApiResponse<AuditHistoryEntry[]>> {
  return apiRequest<AuditHistoryEntry[]>(`/users/${id}/audit-history`);
}
